import { AlertTriangle } from 'lucide-react'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import type { Column } from '@/components/ui/DataTable'
import { Select } from '@/components/ui/Field'
import { StatusBadge } from '@/components/ui/Badge'
import { Chip } from '@/components/ui/Chip'
import { sourceLinkColumn, statusColumn } from '@/components/review/columns'
import { FlagToggle } from '@/components/review/FlagToggle'
import { PreviouslyRejectedFlag } from '@/components/review/VerdictHistory'
import type { ContentStatus, Program, ProgramRef, Segment, SlateProgram, WbProgram, WbProgramTarget } from '@/types'
import { missingLabel } from './ProgramContentEditor'
import { degreeList, distinct, groupStatus, memberIds, type ProgramGroup } from './groups'

const faint = (text: string) => <span className="text-[12px] text-ink-faint">{text}</span>

/** "undergraduate" -> "Undergraduate". The extraction writes lowercase. */
export function levelLabel(level: string | null) {
  return level ? level.charAt(0).toUpperCase() + level.slice(1) : null
}

/** "Nursing (BSN)", or the term alone when no degree was extracted. */
export function refLabel(ref: ProgramRef) {
  return ref.degree ? `${ref.wbProgram} (${ref.degree})` : ref.wbProgram
}

/** A crawled program's label: its WB program, or its crawled name while unassigned, and its degree. */
export function programLabel(p: Program) {
  return refLabel({ ...p, wbProgram: p.wbProgram ?? p.name })
}

/**
 * Mapped to a disabled slate program: ADR-0016 § 6 drops these from
 * downstream flows, so the page says so rather than letting them look mapped.
 */
export function SlateCell({ slate }: { slate: WbProgram['slateProgram'] }) {
  if (!slate) return faint('Unmapped')
  if (!slate.enabled) {
    return (
      <span className="flex items-center gap-1.5 text-[12px] text-warn" title="Mapped to a disabled slate program">
        <AlertTriangle size={12} aria-hidden />
        <span className="truncate">{slate.slateName}</span>
        <span className="sr-only">(disabled)</span>
      </span>
    )
  }
  return <span className="text-[12px]">{slate.slateName}</span>
}

function wbProgramColumn(): Column<Program> {
  return {
    key: 'wbProgram',
    header: 'WB program',
    sortBy: (p) => (p.wbProgram ?? '').toLowerCase(),
    render: (p) => (
      <span className="flex items-center gap-2">
        {p.wbProgram ? <span className="font-head text-[13px] font-semibold">{p.wbProgram}</span> : faint('Unassigned')}
        {p.previouslyRejected && <PreviouslyRejectedFlag compact />}
      </span>
    ),
  }
}

function crawledNameColumn(header = 'Program (as crawled)'): Column<Program> {
  return {
    key: 'name',
    header,
    width: '180px',
    sortBy: (p) => p.name.toLowerCase(),
    render: (p) => <span className="text-[12px] text-ink-muted">{p.name}</span>,
  }
}

function degreeColumn(): Column<Program> {
  return {
    key: 'degree',
    header: 'Degree',
    width: '100px',
    sortBy: (p) => p.degree ?? '',
    render: (p) => (p.degree ? <span className="text-[12px]">{p.degree}</span> : faint('—')),
  }
}

/** A content summary as the tables show it (PC-9): status, and how much is missing. */
export interface ContentSummary {
  status: ContentStatus
  missing: string[]
  /** Slate programs only: false while it shows its WB program's content. */
  own?: boolean
}

const CONTENT_ORDER: Record<ContentStatus, number> = { not_started: 0, generating: 1, in_review: 2, ready: 3 }

function contentColumn<T>(summaryOf: (row: T) => ContentSummary | undefined, inheritable = false): Column<T> {
  return {
    key: 'content',
    header: 'Content',
    width: '170px',
    sortBy: (row) => {
      const summary = summaryOf(row)
      return summary ? CONTENT_ORDER[summary.status] * 10 - summary.missing.length : -1
    },
    render: (row) => {
      const summary = summaryOf(row)
      if (!summary) return faint('—')
      return (
        <span className="flex flex-col gap-0.5" title={summary.missing.length ? `Missing: ${missingLabel(summary.missing)}` : undefined}>
          <span className="flex items-center gap-1.5">
            <StatusBadge status={summary.status} />
            {inheritable && summary.own === false && <span className="text-[11px] text-ink-faint">Inherited</span>}
          </span>
          {summary.missing.length > 0 && summary.status !== 'not_started' && (
            <span className="text-[11px] text-warn">{summary.missing.length} missing</span>
          )}
        </span>
      )
    },
  }
}

/** Up to `max` values, then "+N", with every value in the tooltip. */
function joinedCell(values: string[], max = 3) {
  if (values.length === 0) return faint('—')
  const shown = values.slice(0, max).join(', ')
  return (
    <span className="text-[12px]" title={values.join('\n')}>
      {shown}
      {values.length > max && <span className="text-ink-faint"> +{values.length - max}</span>}
    </span>
  )
}

/**
 * Handlers arrive through `params` rather than closures, which is the cost of
 * moving columns out of the page (docs/06-component-refactor.md §9).
 *
 * One row per WB program (groups.ts). Name, segment and slate mapping are the
 * WB program's; the other cells combine its crawled programs, and the flag
 * writes every one of them. A WB program added by hand has none.
 *
 * Degree and level are the extracted text. `program_type` has no rows yet, so
 * the type column is gone until it does.
 */
export function buildColumns({
  segments,
  onSegmentChange,
  onFlag,
  contentOf = () => undefined,
}: {
  segments: Segment[]
  onSegmentChange: (wbProgramId: string, segmentId: string | null) => void
  onFlag: (ids: string[], flagged: boolean) => void
  /** The WB program's content summary (PC-9). */
  contentOf?: (wbProgramId: string) => ContentSummary | undefined
}): Column<ProgramGroup>[] {
  const segmentName = (id: string | null) => segments.find((s) => s.id === id)?.name ?? 'Unassigned'

  return [
    {
      key: 'flagged',
      header: '',
      width: '36px',
      // Flagged when any degree is; toggling sets or clears all of them.
      render: (g) =>
        g.members.length > 0 && (
          <FlagToggle flagged={g.members.some((m) => m.flagged)} onChange={(next) => onFlag(memberIds(g), next)} />
        ),
    },
    {
      key: 'wbProgram',
      header: 'WB program',
      sortBy: (g) => g.wb.name.toLowerCase(),
      render: (g) => (
        <span className="flex items-center gap-2">
          <span className="font-head text-[13px] font-semibold">{g.wb.name}</span>
          {g.members.some((m) => m.previouslyRejected) && <PreviouslyRejectedFlag compact />}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Programs (as crawled)',
      width: '180px',
      sortBy: (g) => g.members[0]?.name.toLowerCase() ?? '',
      render: (g) =>
        g.members.length === 0
          ? faint(g.wb.programCount === 0 ? 'Added by hand' : 'No program page')
          : joinedCell(distinct(g.members.map((m) => m.name)), 2),
    },
    {
      key: 'degree',
      header: 'Degrees',
      width: '160px',
      sortBy: (g) => degreeList(g),
      render: (g) => {
        const degrees = degreeList(g)
        return degrees ? <span className="text-[12px]">{degrees}</span> : faint('—')
      },
    },
    {
      key: 'level',
      header: 'Level',
      width: '120px',
      sortBy: (g) => g.members[0]?.level ?? '',
      render: (g) => joinedCell(distinct(g.members.map((m) => levelLabel(m.level))), 2),
    },
    {
      key: 'segment',
      header: 'Segment',
      width: '190px',
      sortBy: (g) => segmentName(g.wb.segmentId),
      // Inline select is the primary reassignment path and is keyboard
      // accessible; the bulk bar is the second. Drag is never the only way.
      render: (g) => (
        <Select
          value={g.wb.segmentId ?? ''}
          aria-label={`Segment for ${g.wb.name}`}
          className="h-7 text-[12px]"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation()
            onSegmentChange(g.wb.id, e.target.value || null)
          }}
        >
          <option value="">Unassigned</option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'slate',
      header: 'Slate program',
      width: '210px',
      sortBy: (g) => g.wb.slateProgram?.slateName ?? '',
      // Its own slate program (PC-37), not the slate programs mapped to it.
      render: (g) => <SlateCell slate={g.wb.slateProgram} />,
    },
    contentColumn<ProgramGroup>((g) => contentOf(g.wb.id)),
    sourceLinkColumn<ProgramGroup>(
      (g) => g.members.map((m) => m.programUrl ?? m.sourceUrl).find((url) => url !== null) ?? null,
      { header: 'Page', label: (g) => `Open the program page for ${g.wb.name}` },
    ),
    {
      key: 'tags',
      header: 'Tags',
      width: '160px',
      render: (g) => {
        const tags = distinct(g.members.flatMap((m) => m.tags))
        return (
          <span className="flex flex-wrap gap-1">
            {tags.slice(0, 2).map((t) => (
              <Chip key={t} label={t} />
            ))}
            {tags.length > 2 && <span className="text-[11px] text-ink-faint">+{tags.length - 2}</span>}
          </span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      sortBy: (g) => groupStatus(g) ?? '',
      render: (g) => {
        const status = groupStatus(g)
        return status ? <StatusBadge status={status} /> : faint('—')
      },
    },
  ]
}

/** Each WB program as a picker offers it, by name. */
export function wbProgramOptions(wbPrograms: WbProgram[]): ComboboxOption[] {
  return [...wbPrograms]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    .map((w) => ({ value: w.id, label: w.name }))
}

/**
 * Picks a WB program for crawled programs, or names a new one: the Unassigned
 * tab's and the drawer's "Assign" (PC-33). Clicks stop here: it sits in rows.
 */
export function WbProgramPicker({
  value,
  options,
  onAssign,
  label,
  className = 'h-7 text-[12px]',
}: {
  value: string | null
  options: ComboboxOption[]
  onAssign: (target: WbProgramTarget) => void
  label: string
  className?: string
}) {
  return (
    <Combobox
      value={value}
      options={options}
      onChange={(id) => onAssign(id ? { wbProgramId: id } : null)}
      onCreate={(name) => onAssign({ name })}
      noneLabel="Unassigned"
      searchLabel="Search or name a WB program"
      aria-label={label}
      className={className}
    />
  )
}

/** Crawled programs under no WB program: one picks a WB program for each, or names a new one. */
export function buildUnassignedColumns({
  wbOptions,
  onAssign,
}: {
  wbOptions: ComboboxOption[]
  onAssign: (program: Program, target: WbProgramTarget) => void
}): Column<Program>[] {
  return [
    crawledNameColumn('Program (as crawled)'),
    degreeColumn(),
    {
      key: 'level',
      header: 'Level',
      width: '120px',
      sortBy: (p) => p.level ?? '',
      render: (p) => (p.level ? <span className="text-[12px]">{levelLabel(p.level)}</span> : faint('—')),
    },
    {
      key: 'assign',
      header: 'WB program',
      width: '280px',
      render: (p) => (
        <WbProgramPicker
          value={null}
          options={wbOptions}
          onAssign={(target) => onAssign(p, target)}
          label={`WB program for ${programLabel(p)}`}
        />
      ),
    },
    sourceLinkColumn<Program>((p) => p.programUrl ?? p.sourceUrl, {
      header: 'Page',
      label: (p) => `Open the program page for ${p.name}`,
    }),
    statusColumn<Program>(),
  ]
}

/** Duplicates consolidated into another program. Read-only until un-merging exists. */
export function buildMergedColumns(): Column<Program>[] {
  return [
    crawledNameColumn(),
    degreeColumn(),
    {
      key: 'mergedInto',
      header: 'Merged into',
      width: '220px',
      sortBy: (p) => p.mergedInto?.name ?? '',
      render: (p) => <span className="text-[12px]">{p.mergedInto?.name}</span>,
    },
    sourceLinkColumn<Program>((p) => p.sourceUrl, {
      header: 'Found on',
      showPath: true,
      label: (p) => `Open the page ${p.name} was found on`,
    }),
    statusColumn<Program>(),
  ]
}

/**
 * Programs with no page of their own, usually a mention on a listing page.
 * The page they were found on is the only link there is.
 */
export function buildOtherColumns(): Column<Program>[] {
  return [
    wbProgramColumn(),
    crawledNameColumn(),
    degreeColumn(),
    sourceLinkColumn<Program>((p) => p.sourceUrl, {
      header: 'Found on',
      showPath: true,
      label: (p) => `Open the page ${p.name} was found on`,
    }),
    statusColumn<Program>(),
  ]
}

/**
 * Graduate programs (PC-25): crawled rows under no WB program, reviewed one by
 * one. Not in any output yet, so no segment, slate or content.
 */
export function buildGraduateColumns({
  onFlag,
}: {
  onFlag: (id: string, flagged: boolean) => void
}): Column<Program>[] {
  return [
    {
      key: 'flagged',
      header: '',
      width: '36px',
      render: (p) => <FlagToggle flagged={p.flagged} onChange={(next) => onFlag(p.id, next)} />,
    },
    {
      key: 'name',
      header: 'Program (as crawled)',
      sortBy: (p) => p.name.toLowerCase(),
      render: (p) => (
        <span className="flex items-center gap-2">
          <span className="font-head text-[13px] font-semibold">{p.name}</span>
          {p.previouslyRejected && <PreviouslyRejectedFlag compact />}
        </span>
      ),
    },
    degreeColumn(),
    {
      key: 'department',
      header: 'Department',
      width: '200px',
      sortBy: (p) => p.department ?? '',
      render: (p) => (p.department ? <span className="text-[12px]">{p.department}</span> : faint('—')),
    },
    sourceLinkColumn<Program>((p) => p.programUrl ?? p.sourceUrl, {
      header: 'Page',
      label: (p) => `Open the program page for ${p.name}`,
    }),
    statusColumn<Program>(),
  ]
}

/**
 * The reverse view: each slate program and the WB program it maps to
 * (ADR-0019 § 6), picked inline. The crawled programs whose own older link
 * names it are shown for reference.
 */
export function buildSlateColumns({
  wbOptions,
  onMap,
  contentOf = () => undefined,
}: {
  wbOptions: ComboboxOption[]
  onMap: (slate: SlateProgram, wbProgramId: string | null) => void
  /** The slate program's shown content summary, its own or inherited (PC-9). */
  contentOf?: (slateProgramId: string) => ContentSummary | undefined
}): Column<SlateProgram>[] {
  return [
    {
      key: 'slateName',
      header: 'Slate program',
      sortBy: (s) => s.slateName.toLowerCase(),
      render: (s) => <span className="font-head text-[13px] font-semibold">{s.slateName}</span>,
    },
    {
      key: 'wbProgram',
      header: 'WB program',
      width: '280px',
      sortBy: (s) => s.wbProgram?.name ?? '',
      render: (s) => (
        <Combobox
          value={s.wbProgram?.id ?? null}
          options={wbOptions}
          onChange={(wbProgramId) => onMap(s, wbProgramId)}
          noneLabel="Unmapped"
          searchLabel="Search WB programs"
          aria-label={`WB program for ${s.slateName}`}
          className="h-7 text-[12px]"
        />
      ),
    },
    contentColumn<SlateProgram>((sp) => contentOf(sp.id), true),
    {
      key: 'mapped',
      header: 'Crawled programs',
      width: '280px',
      sortBy: (s) => s.mappedPrograms.length,
      render: (s) =>
        s.mappedPrograms.length === 0 ? (
          faint('None')
        ) : (
          <span className="text-[12px]" title={s.mappedPrograms.map(refLabel).join('\n')}>
            <span className="tabular font-semibold">{s.mappedPrograms.length}</span>
            <span className="text-ink-muted"> · {s.mappedPrograms.map(refLabel).join(', ')}</span>
          </span>
        ),
    },
    {
      key: 'enabled',
      header: 'Enabled',
      width: '100px',
      sortBy: (s) => (s.enabled ? 0 : 1),
      render: (s) => (s.enabled ? <span className="text-[12px] text-affirm">Enabled</span> : faint('Disabled')),
    },
  ]
}
